import { IconButton, makeStyles, Tab, Tabs, withStyles } from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import { useIO, useLinked, useModel } from "@type-r/react";
import { InblockIcon } from "common/invoice-aging/iconography";
import { CollapsableSection, Column, div, Flex, Loading } from "lqx-react";
import React from 'react';
import { ExternalSystemRequest, ExternalSystemType } from "../api/externalSystemRequest";
import { UnappliedCash } from "../api/unappliedCash";
import { HeaderTitle } from "../components/asset-details-pane-styles";
import { CopyRow } from "../components/copy-row";
import { ActivityLog } from '../invoices/invoice-activity-logs-tab';

export const UnappliedCashDetailsPane = ({ unappliedCash, close, unappliedCashId, $selectedTab }: { unappliedCash: UnappliedCash, close, unappliedCashId, $selectedTab }) => {
    const classes = useRemittanceNoteDetailsPaneStyles();

    const $doSendToExternalSystem = useLinked(false);
    const $setSendToExternalType = useLinked(ExternalSystemType.none);
    const externalSystemRequest = useModel(ExternalSystemRequest);
    const inblockedIconClases = useInblockedIconStyles();

    const isSendToExternalSystemReady = useIO(
        async () =>
            $doSendToExternalSystem.isTruthy &&
            $setSendToExternalType.value != ExternalSystemType.none &&
            await sendToExternalSystem($setSendToExternalType.value),
        [$doSendToExternalSystem.value]
    );

    const sendToExternalSystem = async (externalSystem: string) => {
        try {
            if (!unappliedCash.uid) return;

            externalSystemRequest.set({
                to: externalSystem,
                assetType: "UnappliedCash",
                assetUids: [unappliedCash.uid]
            });
            await externalSystemRequest.save();
        }
        finally {
            $doSendToExternalSystem.set(false);
        }
    };

    return isSendToExternalSystemReady ?
        <>
            <Container>
                <Header>
                    <IconButton aria-label="Close" size="small" className={classes.iconButton} onClick={() => close()}>
                        <CloseIcon />
                    </IconButton>
                    <Flex alignCenter>
                        {unappliedCash.isDigitized ? <>
                            <InblockIcon src="/assets/images/inblock_logo.png" classes={inblockedIconClases} />
                            <StatusWithoutMargin>{unappliedCash.state.name}</StatusWithoutMargin> </>
                            : <Status>{unappliedCash.state.name}</Status>}
                    </Flex>
                    <HeaderTitle><>Unapplied Cash {unappliedCash.number ? unappliedCash.number : ''}</></HeaderTitle>
                </Header>
                <RemittanceNoteTabs value={$selectedTab.value}
                    variant="scrollable"
                    scrollButtons="auto"
                    onChange={(evt, newValue) => $selectedTab.set(newValue)} >
                    <RemittanceNoteTab value="details" label=" Details" />
                    <RemittanceNoteTab value="activityLog" label="Activity Log" />
                </RemittanceNoteTabs>
                <RemittanceNoteTabContent>
                    {$selectedTab.value === 'details' && <Details unappliedCash={unappliedCash} unappliedCashId={unappliedCashId} />}
                    {$selectedTab.value === 'activityLog' && <ActivityLog assetId={`${unappliedCashId}`} />}
                </RemittanceNoteTabContent>
            </Container>
        </> : <Loading />
};

const Details = ({ unappliedCash, unappliedCashId }: { unappliedCash: UnappliedCash, unappliedCashId }) => {
    const sectionStyles = SectionStyles();

    return (<>
        <CollapsableSection title="General" titleClassName={sectionStyles.sectionTitle} noBorder noShadow>
            <Column>
                <CopyRow label="Payor" value={unappliedCash.payor.company.name} />
                <CopyRow label="Payee" value={unappliedCash.payee.company.name} />
                <CopyRow label="Amount" currency value={unappliedCash.amount} />
                <CopyRow label="Currency" value={unappliedCash.currency} />
                <CopyRow label="Effective Date" date={!!unappliedCash.effectiveDate} value={unappliedCash.effectiveDate} />
                <CopyRow label="Reference Number" value={unappliedCash.referenceNumber} />
                <CopyRow label="Asset ID" value={unappliedCashId} />
                <CopyRow label="Asset UID" value={unappliedCash.uid} />
                <CopyRow label="Import Job ID" value={unappliedCash.importJobId} hyperlinkHref={`/import-jobs/?importJobId=${unappliedCash.importJobId}`} />
                <CopyRow label="Created Date" date={!!unappliedCash.createdOn} value={unappliedCash.createdOn} />
                <CopyRow label="Updated Date" date={!!unappliedCash.updatedOn} value={unappliedCash.updatedOn} />
            </Column>
        </CollapsableSection>
    </>);
}

const Container = div({
    backgroundColor: 'white',
    height: '100%',
    padding: '0px',
    marginTop: '26px',
});

const Header = div({
    display: 'contents',
    width: '100%',
    height: '100%',
    border: '1px solid #E2F0F7',
    paddingTop: 0
});

const Status = div({
    color: '#63a9dc',
    height: '15px',
    fontSize: '12px',
    marginLeft: '21px',
    fontWeight: 500,
    lineHeight: '15px',
});

const StatusWithoutMargin = div({
    color: '#63a9dc',
    height: '15px',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '15px',
});

const RemittanceNoteTabs = withStyles({
    indicator: {
        display: 'none'
    },
    root: {
        marginTop: '8px'
    }
})((props: any) => <Tabs {...props} TabIndicatorProps={{ children: <span /> }} />);

const RemittanceNoteTab = withStyles({
    root: {
        minWidth: '73px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        padding: '3px 10px 6px',
        position: 'static',
        height: '26px',
        left: '46.58px',
        background: (props: any) => props.selected ? '#DCEEFD' : 'white',
        fontWeight: (props: any) => props.selected ? 600 : 400,
        borderRadius: '4px',
        flex: 'none',
        margin: '0px 20px',
        textTransform: 'none'
    },
})((props: any) => <Tab disableRipple {...props} />);

const RemittanceNoteTabContent = div({
    border: '1px solid #E2F0F7',
    marginTop: '10px',
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '15px',
});

const useRemittanceNoteDetailsPaneStyles = makeStyles({
    iconButton: {
        display: 'block',
        marginLeft: 'auto',
        marginTop: '10px',
        marginRight: '10px',
    },
    textAlignRight: {
        textAlign: 'right',
        paddingRight: '20px',
    }
})

const useLiquidxIconStyles = makeStyles({
    root: {
        display: 'flex',
        width: '140px',
        marginTop: '30px'
    }
});

const useInblockedIconStyles = makeStyles({
    root: {
        display: 'flex',
        width: '15px',
        marginRight: '2px',
        marginLeft: '21px',
    }
});

const SectionStyles = makeStyles({
    sectionTitle: {
        color: '#63a9dc',
        fontSize: '13px',
        marginRight: '1em',
        display: 'flex',
        alignItems: 'center'
    },
})

